"use client";

import { ReactNode, useState } from "react";
import { Button } from "./button";

interface NavItem {
  label: string;
  href: string;
  dropdown?: Array<{ label: string; href: string }>;
}

interface HeaderProps {
  logo?: ReactNode;
  logoHref?: string;
  navItems?: NavItem[];
  cta?: { label: string; href: string; variant?: 'primary' | 'secondary' | 'outline' | 'ghost'; external?: boolean };
  className?: string;
}

export const Header = ({
  logo = (
    <img
      src="https://cdn.prod.website-files.com/65154e68ae1ad53c6e8aaa1a/6516a44d8f77816b807f929d_Logo.svg"
      alt="ALLI"
      className="h-10 md:h-12 w-auto"
    />
  ),
  logoHref = "/",
  navItems = [
    { label: "How it works", href: "/#how-it-works" },
    { label: "Therapists", href: "/therapists" },
    { label: "Pricing", href: "/#pricing" },
    { label: "Login", href: "/login" }
  ],
  cta = {
    label: "Get matched",
    href: "/quiz",
    variant: "primary" as const
  },
  className = ""
}: HeaderProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm ${className}`}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center">
            <a href={logoHref} className="flex-shrink-0">
              {logo}
            </a>
          </div>
          
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <div key={index} className="relative">
                {item.dropdown ? (
                  <button
                    onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    className="text-gray-700 hover:text-primary-600 px-6 py-2 text-base font-medium transition-colors duration-200 flex items-center gap-1"
                  >
                    {item.label}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                ) : (
                  <a
                    href={item.href}
                    className="text-gray-700 hover:text-primary-600 px-6 py-2 text-base font-medium transition-colors duration-200"
                  >
                    {item.label}
                  </a>
                )}
                {item.dropdown && openDropdown === item.label && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2">
                    {item.dropdown.map((dropItem, dropIndex) => (
                      <a
                        key={dropIndex}
                        href={dropItem.href}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                      >
                        {dropItem.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {cta && (
              <div className="ml-4">
                <Button
                  variant={cta.variant || 'primary'}
                  size="md"
                  onClick={() => {
                    if (cta.external) {
                      window.open(cta.href, '_blank');
                    } else {
                      window.location.href = cta.href;
                    }
                  }}
                  className={(cta.label === "Get started" || cta.label === "Find Your Path" || cta.label === "Join Between Sessions")
                    ? "bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white shadow-md hover:shadow-lg"
                    : cta.label === "Learn About Between Sessions"
                    ? "!bg-[#5E7D5E] hover:!bg-[#4A6B4A] !text-white !border-[#5E7D5E] shadow-md hover:shadow-lg"
                    : "shadow-md hover:shadow-lg"}
                >
                  {cta.label}
                </Button>
              </div>
            )}
          </div>
          
          <div className="md:hidden flex items-center">
            {cta && (
              <Button
                variant={cta.variant || 'primary'}
                size="sm"
                onClick={() => window.location.href = cta.href}
                className={(cta.label === "Get started" || cta.label === "Find Your Path" || cta.label === "Join Between Sessions")
                  ? "bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white"
                  : cta.label === "Learn About Between Sessions"
                  ? "!bg-[#5E7D5E] hover:!bg-[#4A6B4A] !text-white !border-[#5E7D5E]"
                  : ""}
              >
                {cta.label}
              </Button>
            )}
          </div>
        </div>
      </div>
      
    </header>
  );
};